// Rotas falsas de Autorização e auditoria (GIY-056; Parte 5.7, 12.1): catálogo, matriz com duas chaves (mesma pessoa →
// 422 AUTHZ_TWO_KEY_SAME_ACTOR; mesmo papel → 422 AUTHZ_TWO_KEY_DISTINCT_ROLE_REQUIRED), atribuições (rede e tenant),
// regras de SoD, break-glass, recertificação, pendências de duas chaves, point-in-time e trilha com exportação. Regras
// de escopo iguais às do servidor: `/network/*` sem acesso de rede → 403 PLT_NETWORK_SCOPE_REQUIRED; `/tenants/{t}` fora
// dos contextos e sem escopo de rede → 404; permissão pela matriz publicada; propósito nas leituras sensíveis; step-up
// nos atos que o exigem; `If-Match` nos `PUT`.
import type { MeResponse } from '@giyus/api-client';

import type { FakeSessionAuth } from '../auth/FakeAuthProvider';
import {
  type AuditEventResponse,
  type FakeAssignment,
  type FakeAuthzState,
  type FakeMatrixVersion,
  type FakeSodRule,
  type FakeTwoKeyApproval,
  appendAudit,
  assignmentStatusOn,
  etagOf,
  fakeAuthzId,
  permissionsOf,
  publishedMatrix,
  shiftDays,
  shiftHours,
  toApprovalResponse,
  toAssignmentResponse,
  toMatrixResponse,
  toMatrixSummary,
  toSodRuleResponse,
  todayUtc,
} from './authzStore';
import { FakeApiError, jsonResponse, problemResponse, readJsonBody } from './http';

export interface AuthzRouteContext {
  readonly state: FakeAuthzState;
  readonly me: MeResponse;
  readonly correlationId: string | null;
  readonly auth: FakeSessionAuth;
}

const PURPOSES = new Set([
  'VisitConfirmation',
  'ClinicalCare',
  'StudySetup',
  'QualityAudit',
  'PrivacyRequest',
  'Export',
  'Support',
  'LegalRequest',
  'Other',
]);
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const TWO_KEY_HOURS = 72;

interface FieldErrorItem {
  readonly field: string;
  readonly code: string;
  readonly message: string;
}

function record(body: unknown): Record<string, unknown> {
  return typeof body === 'object' && body !== null ? (body as Record<string, unknown>) : {};
}

function text(body: unknown, field: string): string | null {
  const value = record(body)[field];
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}

function validation(errors: readonly FieldErrorItem[]): FakeApiError {
  return new FakeApiError(422, 'PLT_VALIDATION', { errors });
}

function requireStepUp(ctx: AuthzRouteContext): void {
  if (!ctx.auth.isRecentlyAuthenticated()) {
    throw new FakeApiError(401, 'PLT_STEP_UP_REQUIRED', { error: 'step_up_required' });
  }
}

function requirePurpose(request: Request): string {
  const header = request.headers.get('X-Purpose');
  if (!header) {
    throw new FakeApiError(422, 'AUTHZ_PURPOSE_REQUIRED');
  }
  const [code, ...rest] = header.split(';');
  const purposeCode = (code ?? '').trim();
  const hasText = rest.join(';').includes('text=');
  if (!PURPOSES.has(purposeCode) || (purposeCode === 'Other' && !hasText)) {
    throw new FakeApiError(422, 'AUTHZ_PURPOSE_INVALID');
  }
  return purposeCode;
}

function requireIfMatch(request: Request, current: string): void {
  const header = request.headers.get('If-Match');
  if (!header) {
    throw new FakeApiError(412, 'PLT_PRECONDITION_REQUIRED');
  }
  if (header !== '*' && header !== current) {
    throw new FakeApiError(412, 'PLT_PRECONDITION_FAILED');
  }
}

function networkRoles(ctx: AuthzRouteContext): readonly string[] {
  return ctx.me.networkAccess.roles;
}

function tenantRoles(ctx: AuthzRouteContext, tenantId: string): readonly string[] {
  const context = ctx.me.contexts.find((item) => item.tenantId === tenantId);
  return [...(context?.effectiveRoles.map((role) => role.role) ?? []), ...networkRoles(ctx)];
}

function requireNetworkPermission(ctx: AuthzRouteContext, permission: string): void {
  if (!permissionsOf(ctx.state, networkRoles(ctx)).has(permission)) {
    throw new FakeApiError(403, 'PLT_FORBIDDEN');
  }
}

function requireTenantPermission(
  ctx: AuthzRouteContext,
  tenantId: string,
  permission: string,
): void {
  if (!permissionsOf(ctx.state, tenantRoles(ctx, tenantId)).has(permission)) {
    throw new FakeApiError(403, 'PLT_FORBIDDEN');
  }
}

function tenantVisible(ctx: AuthzRouteContext, tenantId: string): void {
  const visible =
    ctx.me.networkAccess.hasAccess ||
    ctx.me.contexts.some((context) => context.tenantId === tenantId);
  if (!visible) {
    throw new FakeApiError(404, 'PLT_NOT_FOUND');
  }
}

function actorRoleSnapshot(roles: readonly string[]): string {
  return JSON.stringify([...roles].sort());
}

function audit(
  ctx: AuthzRouteContext,
  event: {
    eventType: string;
    entityType: string | null;
    entityId: string | null;
    tenantId?: string | null;
    studySiteId?: string | null;
    after?: unknown;
    permission?: string | null;
    purpose?: string | null;
  },
): AuditEventResponse {
  const [module] = event.eventType.split('.');
  return appendAudit(ctx.state, {
    actorUserId: ctx.me.user.id,
    actorDisplayName: ctx.me.user.displayName,
    actorKind: 'user',
    actorRoleSnapshot: actorRoleSnapshot(networkRoles(ctx)),
    actorLinkSnapshot: null,
    tenantId: event.tenantId ?? null,
    studySiteId: event.studySiteId ?? null,
    module: module ?? 'PLT',
    eventType: event.eventType,
    entityType: event.entityType,
    entityId: event.entityId,
    before: null,
    after: event.after === undefined ? null : JSON.stringify(event.after),
    changeReasonCode: null,
    changeReasonText: null,
    declaredPurpose: event.purpose ?? null,
    permission: event.permission ?? null,
    correlationId: ctx.correlationId,
  });
}

// ---- Duas chaves ----

function openApproval(
  ctx: AuthzRouteContext,
  actionType: string,
  objectType: string,
  objectId: string,
  requiredSecondPermission: string,
  payload: unknown,
): FakeTwoKeyApproval {
  const pending = ctx.state.approvals.find(
    (item) =>
      item.actionType === actionType &&
      item.objectId === objectId &&
      item.status === 'PendingSecondKey',
  );
  if (pending) {
    throw new FakeApiError(409, 'AUTHZ_TWO_KEY_PENDING');
  }
  const now = new Date().toISOString();
  const approval: FakeTwoKeyApproval = {
    id: fakeAuthzId(ctx.state),
    actionType,
    objectType,
    objectId,
    status: 'PendingSecondKey',
    firstActorUserId: ctx.me.user.id,
    firstAt: now,
    expiresAt: shiftHours(now, TWO_KEY_HOURS),
    secondActorUserId: null,
    secondAt: null,
    requiredSecondPermission,
    requireDistinctRole: true,
    firstActorRoles: [...networkRoles(ctx)],
    payload,
  };
  ctx.state.approvals.push(approval);
  return approval;
}

function completeSecondKey(ctx: AuthzRouteContext, approvalId: string): FakeTwoKeyApproval {
  const approval = ctx.state.approvals.find((item) => item.id === approvalId);
  if (!approval) {
    throw new FakeApiError(404, 'PLT_NOT_FOUND');
  }
  if (approval.status !== 'PendingSecondKey') {
    throw new FakeApiError(409, 'AUTHZ_TWO_KEY_NOT_PENDING');
  }
  if (approval.expiresAt < new Date().toISOString()) {
    throw new FakeApiError(409, 'AUTHZ_TWO_KEY_EXPIRED');
  }
  if (approval.firstActorUserId === ctx.me.user.id) {
    throw new FakeApiError(422, 'AUTHZ_TWO_KEY_SAME_ACTOR');
  }
  requireNetworkPermission(ctx, approval.requiredSecondPermission);
  const callerRoles = networkRoles(ctx);
  if (
    approval.requireDistinctRole &&
    callerRoles.every((role) => approval.firstActorRoles.includes(role))
  ) {
    throw new FakeApiError(422, 'AUTHZ_TWO_KEY_DISTINCT_ROLE_REQUIRED');
  }
  const now = new Date().toISOString();
  approval.status = 'Completed';
  approval.secondActorUserId = ctx.me.user.id;
  approval.secondAt = now;
  if (approval.actionType === 'authz.matrix.publish') {
    applyMatrixPublish(ctx, approval.objectId, now);
  } else if (approval.actionType === 'authz.sod.manage') {
    applySodChange(ctx, approval, now);
  }
  audit(ctx, {
    eventType: 'AUTHZ.TWO_KEY_COMPLETED',
    entityType: 'TwoKeyApproval',
    entityId: approval.id,
    after: { actionType: approval.actionType, objectId: approval.objectId },
    permission: approval.requiredSecondPermission,
  });
  return approval;
}

/** Aprovações pendentes que o chamador pode concluir (permissão do ato) — `mine=false`; `mine=true`: em que é ator. */
function listApprovals(ctx: AuthzRouteContext, mine: boolean, status: string) {
  const permissions = permissionsOf(ctx.state, networkRoles(ctx));
  return ctx.state.approvals
    .filter((item) => item.status === status)
    .filter((item) =>
      mine
        ? item.firstActorUserId === ctx.me.user.id || item.secondActorUserId === ctx.me.user.id
        : item.firstActorUserId !== ctx.me.user.id &&
          permissions.has(item.requiredSecondPermission),
    )
    .map(toApprovalResponse);
}

// ---- Matriz ----

function findVersion(ctx: AuthzRouteContext, id: string | undefined): FakeMatrixVersion {
  const found = ctx.state.matrixVersions.find((version) => version.id === id);
  if (!found) {
    throw new FakeApiError(404, 'PLT_NOT_FOUND');
  }
  return found;
}

function applyMatrixPublish(ctx: AuthzRouteContext, versionId: string, now: string): void {
  const version = findVersion(ctx, versionId);
  for (const current of ctx.state.matrixVersions) {
    if (current.status === 'Published') {
      current.status = 'Superseded';
      current.effectiveTo = now;
      current.rowVersion += 1;
    }
  }
  version.status = 'Published';
  version.effectiveFrom = now;
  version.secondKeyBy = ctx.me.user.id;
  version.rowVersion += 1;
  audit(ctx, {
    eventType: 'AUTHZ.MATRIX_PUBLISHED',
    entityType: 'RolePermissionMatrixVersion',
    entityId: version.id,
    after: { status: 'Published', version: version.version },
    permission: 'authz.matrix.publish',
  });
}

function parseRoles(
  ctx: AuthzRouteContext,
  body: unknown,
): { roleCode: string; permissions: string[] }[] {
  const roles = record(body)['roles'];
  if (!Array.isArray(roles)) {
    throw validation([{ field: 'roles', code: 'required', message: 'Required.' }]);
  }
  const known = new Set(ctx.state.permissions.map((item) => item.code));
  const errors: { field: string; code: string; message: string }[] = [];
  const parsed = roles.map((item, index) => {
    const roleCode = text(item, 'roleCode') ?? '';
    const list = record(item)['permissions'];
    const permissions = Array.isArray(list)
      ? list.filter((p): p is string => typeof p === 'string')
      : [];
    if (!ctx.state.roles.some((role) => role.code === roleCode)) {
      errors.push({
        field: `roles[${String(index)}].roleCode`,
        code: 'unknown_role',
        message: roleCode,
      });
    }
    for (const permission of permissions) {
      if (!known.has(permission)) {
        errors.push({
          field: `roles[${String(index)}].permissions`,
          code: 'unknown_permission',
          message: permission,
        });
      }
    }
    return { roleCode, permissions: [...new Set(permissions)].sort() };
  });
  if (errors.length > 0) {
    throw new FakeApiError(422, 'AUTHZ_MATRIX_INVALID', { errors });
  }
  if (parsed.every((item) => item.permissions.length === 0)) {
    throw new FakeApiError(422, 'AUTHZ_MATRIX_INVALID', {
      errors: [{ field: 'roles', code: 'empty', message: 'Empty.' }],
    });
  }
  return parsed.sort((a, b) => a.roleCode.localeCompare(b.roleCode));
}

async function handleMatrix(
  request: Request,
  rest: readonly string[],
  ctx: AuthzRouteContext,
): Promise<Response> {
  const [id, action, sub] = rest;
  if (!id && request.method === 'GET') {
    requireNetworkPermission(ctx, 'authz.read');
    return jsonResponse(
      [...ctx.state.matrixVersions].sort((a, b) => b.version - a.version).map(toMatrixSummary),
    );
  }
  if (!id && request.method === 'POST') {
    requireNetworkPermission(ctx, 'authz.matrix.edit');
    const body = await readJsonBody(request);
    const current = publishedMatrix(ctx.state);
    const version: FakeMatrixVersion = {
      id: fakeAuthzId(ctx.state),
      version: Math.max(...ctx.state.matrixVersions.map((item) => item.version)) + 1,
      status: 'Draft',
      effectiveFrom: null,
      effectiveTo: null,
      publishedBy: null,
      secondKeyBy: null,
      changeSummary: text(body, 'changeSummary'),
      twoKeyApprovalId: null,
      createdAt: new Date().toISOString(),
      roles: (current?.roles ?? []).map((item) => ({
        roleCode: item.roleCode,
        permissions: [...item.permissions],
      })),
      rowVersion: 1,
    };
    ctx.state.matrixVersions.push(version);
    audit(ctx, {
      eventType: 'AUTHZ.MATRIX_DRAFTED',
      entityType: 'RolePermissionMatrixVersion',
      entityId: version.id,
      after: { version: version.version },
      permission: 'authz.matrix.edit',
    });
    return jsonResponse(toMatrixResponse(version), 201, { ETag: etagOf(version.rowVersion) });
  }
  const version = findVersion(ctx, id);
  if (!action && request.method === 'GET') {
    requireNetworkPermission(ctx, 'authz.read');
    return jsonResponse(toMatrixResponse(version), 200, { ETag: etagOf(version.rowVersion) });
  }
  if (!action && request.method === 'PUT') {
    requireNetworkPermission(ctx, 'authz.matrix.edit');
    if (version.status !== 'Draft') {
      throw new FakeApiError(409, 'AUTHZ_MATRIX_NOT_DRAFT');
    }
    requireIfMatch(request, etagOf(version.rowVersion));
    const body = await readJsonBody(request);
    version.roles = parseRoles(ctx, body);
    version.changeSummary = text(body, 'changeSummary');
    version.rowVersion += 1;
    return jsonResponse(toMatrixResponse(version), 200, { ETag: etagOf(version.rowVersion) });
  }
  if (action === 'publish' && !sub && request.method === 'POST') {
    requireNetworkPermission(ctx, 'authz.matrix.publish');
    requireStepUp(ctx);
    if (version.status !== 'Draft') {
      throw new FakeApiError(409, 'AUTHZ_MATRIX_NOT_DRAFT');
    }
    if (ctx.state.matrixVersions.some((item) => item.status === 'PendingSecondKey')) {
      throw new FakeApiError(409, 'AUTHZ_MATRIX_PUBLISH_PENDING');
    }
    const approval = openApproval(
      ctx,
      'authz.matrix.publish',
      'RolePermissionMatrixVersion',
      version.id,
      'authz.matrix.publish',
      null,
    );
    version.status = 'PendingSecondKey';
    version.publishedBy = ctx.me.user.id;
    version.twoKeyApprovalId = approval.id;
    version.rowVersion += 1;
    audit(ctx, {
      eventType: 'AUTHZ.MATRIX_FIRST_KEY',
      entityType: 'RolePermissionMatrixVersion',
      entityId: version.id,
      after: { twoKeyApprovalId: approval.id },
      permission: 'authz.matrix.publish',
    });
    return jsonResponse(toMatrixResponse(version), 202, { ETag: etagOf(version.rowVersion) });
  }
  if (action === 'publish' && sub === 'second-key' && request.method === 'POST') {
    requireNetworkPermission(ctx, 'authz.matrix.publish');
    requireStepUp(ctx);
    if (version.status !== 'PendingSecondKey' || !version.twoKeyApprovalId) {
      throw new FakeApiError(409, 'AUTHZ_TWO_KEY_NOT_PENDING');
    }
    completeSecondKey(ctx, version.twoKeyApprovalId);
    return jsonResponse(toMatrixResponse(version), 200, { ETag: etagOf(version.rowVersion) });
  }
  throw new FakeApiError(404, 'PLT_NOT_FOUND');
}

// ---- Atribuições ----

function parseGrant(
  ctx: AuthzRouteContext,
  body: unknown,
  scope: { kind: 'network' } | { kind: 'tenant'; tenantId: string },
): FakeAssignment {
  const errors: FieldErrorItem[] = [];
  const userId = text(body, 'userId') ?? '';
  const roleCode = text(body, 'roleCode') ?? '';
  const scopeType = text(body, 'scopeType') ?? '';
  const reason = text(body, 'reason');
  if (!UUID.test(userId)) {
    errors.push({ field: 'userId', code: 'required', message: 'Required.' });
  }
  const role = ctx.state.roles.find((item) => item.code === roleCode);
  if (!role) {
    errors.push({ field: 'roleCode', code: 'unknown', message: 'Unknown role.' });
  }
  if (!['Network', 'Tenant', 'Site', 'Study'].includes(scopeType)) {
    errors.push({ field: 'scopeType', code: 'invalid', message: 'Invalid.' });
  }
  if (!reason) {
    errors.push({ field: 'reason', code: 'required', message: 'Required.' });
  }
  if (scope.kind === 'tenant' && scopeType === 'Network') {
    errors.push({
      field: 'scopeType',
      code: 'network_scope_not_allowed_here',
      message: 'Not here.',
    });
  }
  if (errors.length > 0) {
    throw validation(errors);
  }
  if (userId === ctx.me.user.id) {
    throw new FakeApiError(422, 'AUTHZ_SELF_ASSIGNMENT');
  }
  if (role && !role.allowedScopeTypes.includes(scopeType)) {
    throw new FakeApiError(422, 'AUTHZ_ROLE_SCOPE_NOT_ADMISSIBLE');
  }
  if (scopeType === 'Study' && roleCode !== 'CLINICAL_REVIEWER') {
    throw new FakeApiError(422, 'AUTHZ_STUDY_ROLE_REQUIRES_DELEGATION');
  }
  const scopeId =
    scopeType === 'Network'
      ? null
      : (text(body, 'scopeId') ?? (scope.kind === 'tenant' ? scope.tenantId : null));
  const tenantId =
    scopeType === 'Network'
      ? null
      : scope.kind === 'tenant'
        ? scope.tenantId
        : scopeType === 'Tenant'
          ? scopeId
          : (text(body, 'tenantId') ?? scopeId);
  if (scopeType !== 'Network' && !scopeId) {
    throw validation([{ field: 'scopeId', code: 'required', message: 'Required.' }]);
  }
  if (scope.kind === 'tenant' && scopeType === 'Tenant' && scopeId !== scope.tenantId) {
    throw validation([{ field: 'scopeId', code: 'outside_tenant', message: 'Outside tenant.' }]);
  }
  const today = todayUtc();
  const effectiveFrom = text(body, 'effectiveFrom') ?? today;
  const effectiveTo = text(body, 'effectiveTo');
  if (!ISO_DATE.test(effectiveFrom) || (effectiveTo !== null && !ISO_DATE.test(effectiveTo))) {
    throw validation([{ field: 'effectiveFrom', code: 'invalid', message: 'Invalid date.' }]);
  }
  if (effectiveTo !== null && effectiveTo < effectiveFrom) {
    throw validation([
      { field: 'effectiveTo', code: 'before_effective_from', message: 'Before start.' },
    ]);
  }
  const duplicate = ctx.state.assignments.some(
    (item) =>
      item.userId === userId &&
      item.roleCode === roleCode &&
      item.scopeType === scopeType &&
      item.scopeId === scopeId &&
      assignmentStatusOn(item, today) !== 'Revoked' &&
      assignmentStatusOn(item, today) !== 'Expired',
  );
  if (duplicate) {
    throw new FakeApiError(409, 'AUTHZ_ASSIGNMENT_EXISTS');
  }
  return {
    id: fakeAuthzId(ctx.state),
    userId,
    roleCode,
    scopeType,
    scopeId,
    tenantId,
    effectiveFrom,
    effectiveTo,
    origin: 'Manual',
    reason: reason ?? '',
    grantedBy: ctx.me.user.id,
    grantedAt: new Date().toISOString(),
    linkVerified: false,
    revokedBy: null,
    revokedAt: null,
    revokeReason: null,
    seeded: false,
  };
}

async function grantAssignment(
  request: Request,
  ctx: AuthzRouteContext,
  scope: { kind: 'network' } | { kind: 'tenant'; tenantId: string },
): Promise<Response> {
  requireStepUp(ctx);
  const assignment = parseGrant(ctx, await readJsonBody(request), scope);
  ctx.state.assignments.push(assignment);
  audit(ctx, {
    eventType: 'AUTHZ.ROLE_ASSIGNED',
    entityType: 'RoleAssignment',
    entityId: assignment.id,
    tenantId: assignment.tenantId,
    after: {
      userId: assignment.userId,
      roleCode: assignment.roleCode,
      scopeType: assignment.scopeType,
    },
    permission: scope.kind === 'network' ? 'authz.role.assign' : 'authz.role.assign.tenant',
  });
  return jsonResponse(toAssignmentResponse(assignment, todayUtc()), 201);
}

async function revokeAssignment(
  request: Request,
  ctx: AuthzRouteContext,
  assignmentId: string | undefined,
  tenantId: string | null,
): Promise<Response> {
  requireStepUp(ctx);
  const assignment = ctx.state.assignments.find(
    (item) => item.id === assignmentId && (tenantId === null || item.tenantId === tenantId),
  );
  if (!assignment) {
    throw new FakeApiError(404, 'PLT_NOT_FOUND');
  }
  const body = await readJsonBody(request);
  const reason = text(body, 'reason');
  if (!reason) {
    throw validation([{ field: 'reason', code: 'required', message: 'Required.' }]);
  }
  const status = assignmentStatusOn(assignment, todayUtc());
  if (status === 'Revoked' || status === 'Expired') {
    throw new FakeApiError(409, 'AUTHZ_ASSIGNMENT_NOT_ACTIVE');
  }
  assignment.revokedAt = new Date().toISOString();
  assignment.revokedBy = ctx.me.user.id;
  assignment.revokeReason = reason;
  audit(ctx, {
    eventType: 'AUTHZ.ROLE_REVOKED',
    entityType: 'RoleAssignment',
    entityId: assignment.id,
    tenantId: assignment.tenantId,
    after: { revokeReason: reason },
    permission: tenantId === null ? 'authz.role.assign' : 'authz.role.assign.tenant',
  });
  return jsonResponse(toAssignmentResponse(assignment, todayUtc()));
}

function listAssignments(ctx: AuthzRouteContext, url: URL, tenantId: string | null): Response {
  const today = todayUtc();
  const status = url.searchParams.get('status') ?? 'Active';
  const userId = url.searchParams.get('userId');
  const scopeType = url.searchParams.get('scopeType');
  const scopeId = url.searchParams.get('scopeId');
  const items = ctx.state.assignments
    .filter((item) => tenantId === null || item.tenantId === tenantId)
    .filter((item) => !userId || item.userId === userId)
    .filter((item) => !scopeType || item.scopeType === scopeType)
    .filter((item) => !scopeId || item.scopeId === scopeId)
    .filter((item) => status === 'All' || assignmentStatusOn(item, today) === status)
    .sort((a, b) => b.grantedAt.localeCompare(a.grantedAt))
    .map((item) => toAssignmentResponse(item, today));
  return jsonResponse(items);
}

// ---- SoD ----

function applySodChange(ctx: AuthzRouteContext, approval: FakeTwoKeyApproval, now: string): void {
  const rule = ctx.state.sodRules.find((item) => item.id === approval.objectId);
  if (!rule) {
    return;
  }
  const today = todayUtc(new Date(now));
  if (rule.status === 'PendingSecondKey') {
    rule.status = 'Active';
    rule.effectiveFrom = today;
    rule.secondKeyBy = ctx.me.user.id;
  } else if (rule.pendingEnd) {
    rule.status = 'Ended';
    rule.effectiveTo = today;
    rule.endedBy = ctx.me.user.id;
    rule.endReason = rule.pendingEnd.reason;
    rule.pendingEnd = null;
  }
  rule.twoKeyApprovalId = null;
  rule.rowVersion += 1;
}

async function handleSodRules(
  request: Request,
  rest: readonly string[],
  ctx: AuthzRouteContext,
  url: URL,
): Promise<Response> {
  const [id] = rest;
  if (!id && request.method === 'GET') {
    requireNetworkPermission(ctx, 'authz.read');
    const kind = url.searchParams.get('kind');
    const status = url.searchParams.get('status');
    return jsonResponse(
      ctx.state.sodRules
        .filter((rule) => !kind || rule.kind === kind)
        .filter((rule) => !status || rule.status === status)
        .map(toSodRuleResponse),
    );
  }
  if (!id && request.method === 'POST') {
    requireNetworkPermission(ctx, 'authz.sod.manage');
    requireStepUp(ctx);
    const body = await readJsonBody(request);
    const errors: FieldErrorItem[] = [];
    const kind = text(body, 'kind') ?? '';
    const leftCode = text(body, 'leftCode') ?? '';
    const rightCode = text(body, 'rightCode') ?? '';
    const objectType = text(body, 'objectType') ?? '';
    const description = text(body, 'description') ?? '';
    if (kind !== 'Act' && kind !== 'Assignment') {
      errors.push({ field: 'kind', code: 'invalid', message: 'Invalid.' });
    }
    for (const [field, value] of [
      ['leftCode', leftCode],
      ['rightCode', rightCode],
      ['objectType', objectType],
      ['description', description],
    ] as const) {
      if (!value) {
        errors.push({ field, code: 'required', message: 'Required.' });
      }
    }
    if (kind === 'Act') {
      const known = new Set(ctx.state.permissions.map((item) => item.code));
      for (const [field, value] of [
        ['leftCode', leftCode],
        ['rightCode', rightCode],
      ] as const) {
        if (value && !known.has(value)) {
          errors.push({ field, code: 'unknown_permission', message: value });
        }
      }
    }
    if (errors.length > 0) {
      throw validation(errors);
    }
    if (
      ctx.state.sodRules.some(
        (rule) =>
          rule.kind === kind &&
          rule.leftCode === leftCode &&
          rule.rightCode === rightCode &&
          rule.objectType === objectType &&
          (rule.status === 'Active' || rule.status === 'PendingSecondKey'),
      )
    ) {
      throw new FakeApiError(409, 'AUTHZ_SOD_RULE_EXISTS');
    }
    const rule: FakeSodRule = {
      id: fakeAuthzId(ctx.state),
      kind,
      leftCode,
      rightCode,
      objectType,
      description,
      status: 'PendingSecondKey',
      effectiveFrom: null,
      effectiveTo: null,
      requestedBy: ctx.me.user.id,
      secondKeyBy: null,
      endedBy: null,
      endReason: null,
      twoKeyApprovalId: null,
      createdAt: new Date().toISOString(),
      rowVersion: 1,
      pendingEnd: null,
    };
    const approval = openApproval(ctx, 'authz.sod.manage', 'SodRule', rule.id, 'authz.sod.manage', {
      create: true,
    });
    rule.twoKeyApprovalId = approval.id;
    ctx.state.sodRules.push(rule);
    return jsonResponse(toSodRuleResponse(rule), 202, { ETag: etagOf(rule.rowVersion) });
  }
  const rule = ctx.state.sodRules.find((item) => item.id === id);
  if (!rule) {
    throw new FakeApiError(404, 'PLT_NOT_FOUND');
  }
  if (request.method === 'GET') {
    requireNetworkPermission(ctx, 'authz.read');
    return jsonResponse(toSodRuleResponse(rule), 200, { ETag: etagOf(rule.rowVersion) });
  }
  if (request.method === 'PUT') {
    requireNetworkPermission(ctx, 'authz.sod.manage');
    requireStepUp(ctx);
    if (rule.status !== 'Active') {
      throw new FakeApiError(409, 'AUTHZ_SOD_RULE_NOT_ACTIVE');
    }
    requireIfMatch(request, etagOf(rule.rowVersion));
    if (rule.twoKeyApprovalId) {
      throw new FakeApiError(409, 'AUTHZ_TWO_KEY_PENDING');
    }
    const body = await readJsonBody(request);
    const end = record(body)['end'] === true;
    const description = text(body, 'description');
    const reason = text(body, 'reason');
    if (!end && !description) {
      throw validation([{ field: 'description', code: 'required', message: 'Nothing to change.' }]);
    }
    if (end && !reason) {
      throw validation([{ field: 'reason', code: 'required', message: 'Required.' }]);
    }
    const approval = openApproval(ctx, 'authz.sod.manage', 'SodRule', rule.id, 'authz.sod.manage', {
      end,
      description,
    });
    rule.twoKeyApprovalId = approval.id;
    rule.pendingEnd = end ? { reason: reason ?? '' } : null;
    if (description && !end) {
      rule.description = description;
    }
    rule.rowVersion += 1;
    return jsonResponse(toSodRuleResponse(rule), 202, { ETag: etagOf(rule.rowVersion) });
  }
  throw new FakeApiError(404, 'PLT_NOT_FOUND');
}

// ---- Break-glass ----

async function openBreakGlass(
  request: Request,
  ctx: AuthzRouteContext,
  tenantId: string,
): Promise<Response> {
  requireTenantPermission(ctx, tenantId, 'breakglass.use');
  requireStepUp(ctx);
  const body = await readJsonBody(request);
  const errors: FieldErrorItem[] = [];
  const scopeType = text(body, 'scopeType');
  const scopeId = text(body, 'scopeId') ?? '';
  const reason = text(body, 'reason');
  if (scopeType !== 'Study') {
    errors.push({ field: 'scopeType', code: 'invalid', message: 'Only Study in v1.' });
  }
  if (!UUID.test(scopeId)) {
    errors.push({ field: 'scopeId', code: 'required', message: 'Required.' });
  }
  if (!reason) {
    errors.push({ field: 'reason', code: 'required', message: 'Required.' });
  }
  if (errors.length > 0) {
    throw validation(errors);
  }
  const now = new Date().toISOString();
  if (
    ctx.state.breakGlass.some(
      (event) =>
        event.userId === ctx.me.user.id &&
        event.scopeId === scopeId &&
        event.status === 'Active' &&
        event.expiresAt > now,
    )
  ) {
    throw new FakeApiError(409, 'AUTHZ_BREAK_GLASS_ACTIVE');
  }
  const event = {
    id: fakeAuthzId(ctx.state),
    userId: ctx.me.user.id,
    tenantId,
    scopeType: 'Study',
    scopeId,
    reason: reason ?? '',
    startedAt: now,
    expiresAt: shiftHours(now, 4),
    endedAt: null,
    endReason: null,
    status: 'Active',
    reviewDueAt: shiftHours(now, 48),
    reviewedBy: null,
    reviewedAt: null,
    reviewOutcome: null,
    reviewNote: null,
    reviewOverdue: false,
  };
  ctx.state.breakGlass.push(event);
  // O `/me` do falso reflete o acesso aberto (contexts[].activeBreakGlass: só escopo e prazos, nunca a razão) — a faixa do shell aparece como no servidor real.
  ctx.me.contexts
    .find((context) => context.tenantId === tenantId)
    ?.activeBreakGlass.push({
      scopeType: 'Study',
      scopeId,
      expiresAt: event.expiresAt,
      reviewDueAt: event.reviewDueAt,
    });
  audit(ctx, {
    eventType: 'AUTHZ.BREAK_GLASS_OPENED',
    entityType: 'BreakGlassEvent',
    entityId: event.id,
    tenantId,
    studySiteId: scopeId,
    after: { reason: event.reason },
    permission: 'breakglass.use',
  });
  return jsonResponse(event, 201);
}

async function reviewBreakGlass(
  request: Request,
  ctx: AuthzRouteContext,
  eventId: string | undefined,
): Promise<Response> {
  requireNetworkPermission(ctx, 'breakglass.review');
  const event = ctx.state.breakGlass.find((item) => item.id === eventId);
  if (!event) {
    throw new FakeApiError(404, 'PLT_NOT_FOUND');
  }
  const body = await readJsonBody(request);
  const outcome = text(body, 'outcome');
  if (outcome !== 'Justified' && outcome !== 'Unjustified') {
    throw validation([{ field: 'outcome', code: 'invalid', message: 'Invalid.' }]);
  }
  if (event.userId === ctx.me.user.id) {
    throw new FakeApiError(422, 'AUTHZ_SOD_VIOLATION');
  }
  if (event.reviewedAt) {
    throw new FakeApiError(409, 'AUTHZ_BREAK_GLASS_REVIEWED');
  }
  const now = new Date().toISOString();
  if (outcome === 'Unjustified' && event.status === 'Active') {
    event.status = 'Ended';
    event.endedAt = now;
    event.endReason = 'unjustified';
  }
  event.reviewedAt = now;
  event.reviewedBy = ctx.me.user.id;
  event.reviewOutcome = outcome;
  event.reviewNote = text(body, 'note');
  event.reviewOverdue = false;
  audit(ctx, {
    eventType: 'AUTHZ.BREAK_GLASS_REVIEWED',
    entityType: 'BreakGlassEvent',
    entityId: event.id,
    tenantId: event.tenantId,
    after: { outcome },
    permission: 'breakglass.review',
  });
  return jsonResponse(event);
}

function listBreakGlass(ctx: AuthzRouteContext, url: URL): Response {
  requireNetworkPermission(ctx, 'breakglass.review');
  const status = url.searchParams.get('status') ?? 'PendingReview';
  const tenantId = url.searchParams.get('tenantId');
  const now = new Date().toISOString();
  const items = ctx.state.breakGlass
    .map((event) => ({
      ...event,
      status: event.status === 'Active' && event.expiresAt <= now ? 'Ended' : event.status,
      reviewOverdue: !event.reviewedAt && event.reviewDueAt < now,
    }))
    .filter((event) => !tenantId || event.tenantId === tenantId)
    .filter((event) => {
      switch (status) {
        case 'PendingReview':
          return !event.reviewedAt;
        case 'Active':
          return event.status === 'Active';
        case 'Ended':
          return event.status === 'Ended';
        case 'Reviewed':
          return Boolean(event.reviewedAt);
        default:
          return true;
      }
    })
    .sort((a, b) => b.startedAt.localeCompare(a.startedAt));
  return jsonResponse(items);
}

// ---- Recertificação ----

function myReviewItems(ctx: AuthzRouteContext) {
  return ctx.state.reviewItems.filter(
    (item) =>
      item.status === 'Pending' &&
      item.subjectUserId !== ctx.me.user.id &&
      (item.reviewerUserId === ctx.me.user.id || item.escalatedToUserId === ctx.me.user.id),
  );
}

async function decideReview(
  request: Request,
  ctx: AuthzRouteContext,
  itemId: string | undefined,
): Promise<Response> {
  const item = ctx.state.reviewItems.find((entry) => entry.id === itemId);
  if (!item) {
    throw new FakeApiError(404, 'PLT_NOT_FOUND');
  }
  const body = await readJsonBody(request);
  const decision = text(body, 'decision');
  if (decision !== 'Keep' && decision !== 'Revoke') {
    throw validation([{ field: 'decision', code: 'invalid', message: 'Invalid.' }]);
  }
  if (item.subjectUserId === ctx.me.user.id) {
    throw new FakeApiError(422, 'AUTHZ_SOD_VIOLATION');
  }
  if (item.status === 'Decided') {
    throw new FakeApiError(409, 'AUTHZ_ACCESS_REVIEW_DECIDED');
  }
  if (item.reviewerUserId !== ctx.me.user.id && item.escalatedToUserId !== ctx.me.user.id) {
    throw new FakeApiError(403, 'PLT_FORBIDDEN');
  }
  const now = new Date().toISOString();
  item.status = 'Decided';
  item.decision = decision;
  item.decidedBy = ctx.me.user.id;
  item.decidedAt = now;
  item.decisionNote = text(body, 'note');
  const campaign = ctx.state.campaigns.find((entry) => entry.id === item.campaignId);
  if (campaign) {
    // Contadores int32 chegam como `number | string` no contrato gerado.
    const decided = Number(campaign.decidedCount) + 1;
    campaign.decidedCount = decided;
    if (decision === 'Keep') {
      campaign.keptCount = Number(campaign.keptCount) + 1;
    } else {
      campaign.revokedCount = Number(campaign.revokedCount) + 1;
    }
    if (decided >= Number(campaign.itemCount)) {
      campaign.status = 'Closed';
      campaign.closedAt = now;
    }
  }
  if (decision === 'Revoke') {
    const assignment = ctx.state.assignments.find((entry) => entry.id === item.subjectId);
    if (assignment && !assignment.revokedAt) {
      assignment.revokedAt = now;
      assignment.revokedBy = ctx.me.user.id;
      assignment.revokeReason = 'Recertificação de acessos: revogado pelo gestor';
    }
  }
  audit(ctx, {
    eventType: 'AUTHZ.ACCESS_REVIEW_DECIDED',
    entityType: 'AccessReviewItem',
    entityId: item.id,
    tenantId: item.tenantId,
    after: { decision },
    permission: 'access.review.self',
  });
  return jsonResponse(item);
}

// ---- Point-in-time ----

function pointInTime(request: Request, ctx: AuthzRouteContext, url: URL): Response {
  requireNetworkPermission(ctx, 'authz.history.read');
  const purpose = requirePurpose(request);
  const date = url.searchParams.get('date');
  if (!date) {
    throw validation([{ field: 'date', code: 'required', message: 'Required.' }]);
  }
  if (!ISO_DATE.test(date)) {
    throw validation([{ field: 'date', code: 'invalid', message: 'Invalid.' }]);
  }
  if (date > todayUtc()) {
    throw validation([{ field: 'date', code: 'in_future', message: 'Future.' }]);
  }
  const userId = url.searchParams.get('userId');
  const endOfDay = `${shiftDays(date, 1)}T00:00:00.000Z`;
  const items = ctx.state.assignments
    .filter((item) => !userId || item.userId === userId)
    .filter(
      (item) =>
        item.grantedAt < endOfDay &&
        item.effectiveFrom <= date &&
        (item.effectiveTo === null || item.effectiveTo >= date) &&
        (item.revokedAt === null || item.revokedAt >= endOfDay),
    )
    .sort((a, b) => a.userId.localeCompare(b.userId) || a.id.localeCompare(b.id))
    .map((item) => ({
      assignmentId: item.id,
      userId: item.userId,
      roleCode: item.roleCode,
      scopeType: item.scopeType,
      scopeId: item.scopeId,
      tenantId: item.tenantId,
      effectiveFrom: item.effectiveFrom,
      effectiveTo: item.effectiveTo,
      origin: item.origin,
      reason: item.reason,
      grantedBy: item.grantedBy,
      grantedAt: item.grantedAt,
      revokedAt: item.revokedAt,
      revokedBy: item.revokedBy,
    }));
  const matrix = ctx.state.matrixVersions.find(
    (version) =>
      (version.status === 'Published' || version.status === 'Superseded') &&
      version.effectiveFrom !== null &&
      version.effectiveFrom < endOfDay &&
      (version.effectiveTo === null || version.effectiveTo >= endOfDay),
  );
  const rolePermissions: Record<string, string[]> = {};
  for (const roleCode of new Set(items.map((item) => item.roleCode))) {
    rolePermissions[roleCode] = [
      ...(matrix?.roles.find((entry) => entry.roleCode === roleCode)?.permissions ?? []),
    ];
  }
  audit(ctx, {
    eventType: 'AUTHZ.HISTORY_QUERIED',
    entityType: 'PointInTimeReport',
    entityId: null,
    after: { date, userId },
    permission: 'authz.history.read',
    purpose,
  });
  return jsonResponse({
    date,
    matrix: matrix
      ? {
          versionId: matrix.id,
          version: matrix.version,
          effectiveFrom: matrix.effectiveFrom,
          effectiveTo: matrix.effectiveTo,
        }
      : null,
    rolePermissions,
    items,
    delegations: [],
    nextCursor: null,
  });
}

// ---- Auditoria ----

type AuditScope =
  | { kind: 'network' }
  | { kind: 'tenant'; tenantId: string }
  | { kind: 'study'; tenantId: string; studySiteId: string };

function auditRows(ctx: AuthzRouteContext, url: URL, scope: AuditScope): AuditEventResponse[] {
  const get = (name: string) => url.searchParams.get(name);
  const accessible = new Set(
    ctx.me.contexts.flatMap((context) =>
      context.effectiveRoles.filter((r) => r.scopeType === 'Study').map((r) => r.scopeId),
    ),
  );
  return ctx.state.audit
    .filter((row) => {
      if (scope.kind === 'network') {
        return !get('tenantId') || row.tenantId === get('tenantId');
      }
      if (scope.kind === 'tenant') {
        return (
          row.tenantId === scope.tenantId &&
          (row.studySiteId === null ||
            ctx.me.networkAccess.hasAccess ||
            accessible.has(row.studySiteId))
        );
      }
      return row.tenantId === scope.tenantId && row.studySiteId === scope.studySiteId;
    })
    .filter((row) => !get('entityType') || row.entityType === get('entityType'))
    .filter((row) => !get('entityId') || row.entityId === get('entityId'))
    .filter((row) => !get('personId') || row.personId === get('personId'))
    .filter((row) => !get('actorId') || row.actorUserId === get('actorId'))
    .filter((row) => !get('eventType') || row.eventType === get('eventType'))
    .filter((row) => !get('studySiteId') || row.studySiteId === get('studySiteId'))
    .filter((row) => !get('from') || row.occurredAt >= (get('from') ?? ''))
    .filter((row) => !get('to') || row.occurredAt <= (get('to') ?? ''));
}

function queryAudit(
  request: Request,
  ctx: AuthzRouteContext,
  url: URL,
  scope: AuditScope,
): Response {
  const purpose = requirePurpose(request);
  const rows = auditRows(ctx, url, scope);
  const after = Number(url.searchParams.get('cursor') ?? '0');
  const limit = Math.min(200, Math.max(1, Number(url.searchParams.get('limit') ?? '50')));
  const page = rows.filter((row) => Number(row.sequence) > after).slice(0, limit + 1);
  const hasMore = page.length > limit;
  const items = hasMore ? page.slice(0, limit) : page;
  audit(ctx, {
    eventType: 'AUD.AUDIT_QUERIED',
    entityType: 'AuditEvent',
    entityId: null,
    tenantId: scope.kind === 'network' ? null : scope.tenantId,
    after: { scope: scope.kind },
    permission: scope.kind === 'network' ? 'audit.read.network' : 'audit.read',
    purpose,
  });
  return jsonResponse({
    items,
    nextCursor: hasMore ? String(items[items.length - 1]?.sequence ?? '') : null,
  });
}

const CSV_COLUMNS = [
  'sequence',
  'id',
  'occurredAt',
  'actorUserId',
  'actorKind',
  'actorDisplayName',
  'actorRoleSnapshot',
  'actorLinkSnapshot',
  'tenantId',
  'studySiteId',
  'module',
  'eventType',
  'entityType',
  'entityId',
  'permission',
  'declaredPurpose',
  'changeReasonCode',
  'changeReasonText',
  'correlationId',
  'before',
  'after',
  'previousHash',
  'hash',
] as const;

function csvEscape(value: unknown): string {
  let field: string;
  if (value === null || value === undefined) {
    field = '';
  } else if (typeof value === 'string') {
    field = value;
  } else if (typeof value === 'number' || typeof value === 'boolean') {
    field = String(value);
  } else {
    field = JSON.stringify(value);
  }
  return /[",\r\n]/.test(field) ? `"${field.replace(/"/g, '""')}"` : field;
}

function exportAudit(
  request: Request,
  ctx: AuthzRouteContext,
  url: URL,
  scope: AuditScope,
): Response {
  requireStepUp(ctx);
  const purpose = requirePurpose(request);
  const format = (url.searchParams.get('format') ?? 'jsonl').toLowerCase();
  if (format !== 'jsonl' && format !== 'csv') {
    throw validation([{ field: 'format', code: 'invalid', message: 'jsonl|csv' }]);
  }
  const rows = auditRows(ctx, url, scope);
  if (rows.length > 100_000) {
    throw new FakeApiError(422, 'AUD_EXPORT_TOO_LARGE');
  }
  const scopeName =
    scope.kind === 'network'
      ? 'network'
      : scope.kind === 'tenant'
        ? `tenant-${scope.tenantId}`
        : `study-${scope.studySiteId}`;
  audit(ctx, {
    eventType: 'AUD.AUDIT_EXPORTED',
    entityType: 'AuditEvent',
    entityId: null,
    tenantId: scope.kind === 'network' ? null : scope.tenantId,
    after: { scope: scopeName, format },
    permission: 'audit.export',
    purpose,
  });
  const fileName = `audit-${scopeName}-${Math.floor(Date.now() / 1000).toString()}.${format}`;
  const body =
    format === 'csv'
      ? [
          CSV_COLUMNS.join(','),
          ...rows.map((row) => CSV_COLUMNS.map((column) => csvEscape(row[column])).join(',')),
        ].join('\n') + '\n'
      : [
          ...rows.map((row) => JSON.stringify(row)),
          JSON.stringify({
            kind: 'trailer',
            rows: rows.length,
            sha256: 'fake',
            scope: scopeName,
            purpose,
          }),
        ].join('\n') + '\n';
  return new Response(body, {
    status: 200,
    headers: {
      'content-type':
        format === 'csv' ? 'text/csv; charset=utf-8' : 'application/x-ndjson; charset=utf-8',
      'content-disposition': `attachment; filename="${fileName}"`,
    },
  });
}

// ---- Dispatcher ----

export async function handleAuthzRoute(
  request: Request,
  segments: readonly string[],
  ctx: AuthzRouteContext,
): Promise<Response | null> {
  const url = new URL(request.url);
  try {
    if (segments[0] === 'network' && (segments[1] === 'authz' || segments[1] === 'audit')) {
      if (!ctx.me.networkAccess.hasAccess) {
        throw new FakeApiError(403, 'PLT_NETWORK_SCOPE_REQUIRED');
      }
      if (segments[1] === 'audit') {
        if (segments.length === 2 && request.method === 'GET') {
          requireNetworkPermission(ctx, 'audit.read.network');
          return queryAudit(request, ctx, url, { kind: 'network' });
        }
        if (segments[2] === 'export' && request.method === 'GET') {
          requireNetworkPermission(ctx, 'audit.export');
          return exportAudit(request, ctx, url, { kind: 'network' });
        }
        if (segments[2] === 'chain-status' && request.method === 'GET') {
          requireNetworkPermission(ctx, 'audit.read.network');
          const purpose = requirePurpose(request);
          audit(ctx, {
            eventType: 'AUD.CHAIN_VERIFIED',
            entityType: 'AuditEvent',
            entityId: null,
            after: { eventsChecked: ctx.state.audit.length, isValid: true },
            permission: 'audit.read.network',
            purpose,
          });
          return jsonResponse({
            isValid: true,
            eventsChecked: ctx.state.audit.length,
            firstBrokenSequence: null,
          });
        }
        return null;
      }
      const [, , resource, ...rest] = segments;
      switch (resource) {
        case 'roles':
          requireNetworkPermission(ctx, 'authz.read');
          return jsonResponse(ctx.state.roles);
        case 'permissions':
          requireNetworkPermission(ctx, 'authz.read');
          return jsonResponse(ctx.state.permissions);
        case 'matrix':
          if (rest[0] !== 'versions') {
            return null;
          }
          return await handleMatrix(request, rest.slice(1), ctx);
        case 'assignments':
          if (rest.length === 0 && request.method === 'GET') {
            requireNetworkPermission(ctx, 'authz.assignment.read');
            return listAssignments(ctx, url, null);
          }
          if (rest.length === 0 && request.method === 'POST') {
            requireNetworkPermission(ctx, 'authz.role.assign');
            return await grantAssignment(request, ctx, { kind: 'network' });
          }
          if (rest[1] === 'revoke' && request.method === 'POST') {
            requireNetworkPermission(ctx, 'authz.role.assign');
            return await revokeAssignment(request, ctx, rest[0], null);
          }
          return null;
        case 'sod-rules':
          return await handleSodRules(request, rest, ctx, url);
        case 'break-glass':
          if (rest.length === 0 && request.method === 'GET') {
            return listBreakGlass(ctx, url);
          }
          if (rest[1] === 'review' && request.method === 'POST') {
            return await reviewBreakGlass(request, ctx, rest[0]);
          }
          return null;
        case 'access-reviews':
          requireNetworkPermission(ctx, 'access.review.read');
          if (rest.length === 0 && request.method === 'GET') {
            return jsonResponse(ctx.state.campaigns);
          }
          if (rest[1] === 'items' && request.method === 'GET') {
            const status = url.searchParams.get('status') ?? 'All';
            return jsonResponse(
              ctx.state.reviewItems
                .filter((item) => item.campaignId === rest[0])
                .filter((item) =>
                  status === 'All'
                    ? true
                    : status === 'Escalated'
                      ? Boolean(item.escalatedAt)
                      : item.status === status,
                ),
            );
          }
          return null;
        case 'point-in-time':
          return request.method === 'GET' ? pointInTime(request, ctx, url) : null;
        default:
          return null;
      }
    }

    if (segments[0] === 'authz') {
      if (segments[1] === 'two-key' && segments.length === 2 && request.method === 'GET') {
        const mine = url.searchParams.get('mine') === 'true';
        const status = url.searchParams.get('status') ?? 'PendingSecondKey';
        return jsonResponse(listApprovals(ctx, mine, status));
      }
      if (segments[1] === 'two-key' && segments[3] === 'second-key' && request.method === 'POST') {
        requireStepUp(ctx);
        return jsonResponse(toApprovalResponse(completeSecondKey(ctx, segments[2] ?? '')));
      }
      if (
        segments[1] === 'access-reviews' &&
        segments[2] === 'my-items' &&
        request.method === 'GET'
      ) {
        return jsonResponse(myReviewItems(ctx));
      }
      if (
        segments[1] === 'access-reviews' &&
        segments[2] === 'items' &&
        segments[4] === 'decide' &&
        request.method === 'POST'
      ) {
        return await decideReview(request, ctx, segments[3]);
      }
      return null;
    }

    if (segments[0] === 'tenants' && segments[1]) {
      const tenantId = segments[1];
      if (segments[2] === 'authz') {
        tenantVisible(ctx, tenantId);
        if (segments[3] === 'assignments') {
          if (segments.length === 4 && request.method === 'GET') {
            requireTenantPermission(ctx, tenantId, 'authz.assignment.read.tenant');
            return listAssignments(ctx, url, tenantId);
          }
          if (segments.length === 4 && request.method === 'POST') {
            requireTenantPermission(ctx, tenantId, 'authz.role.assign.tenant');
            return await grantAssignment(request, ctx, { kind: 'tenant', tenantId });
          }
          if (segments[5] === 'revoke' && request.method === 'POST') {
            requireTenantPermission(ctx, tenantId, 'authz.role.assign.tenant');
            return await revokeAssignment(request, ctx, segments[4], tenantId);
          }
        }
        if (segments[3] === 'break-glass' && segments.length === 4 && request.method === 'POST') {
          return await openBreakGlass(request, ctx, tenantId);
        }
        return null;
      }
      if (segments[2] === 'audit') {
        tenantVisible(ctx, tenantId);
        if (segments.length === 3 && request.method === 'GET') {
          requireTenantPermission(ctx, tenantId, 'audit.read');
          return queryAudit(request, ctx, url, { kind: 'tenant', tenantId });
        }
        if (segments[3] === 'export' && request.method === 'GET') {
          requireTenantPermission(ctx, tenantId, 'audit.export');
          return exportAudit(request, ctx, url, { kind: 'tenant', tenantId });
        }
        return null;
      }
      if (segments[2] === 'studies' && segments[3] && segments[4] === 'audit') {
        tenantVisible(ctx, tenantId);
        const studySiteId = segments[3];
        const accessible =
          ctx.me.networkAccess.hasAccess ||
          ctx.me.contexts.some((context) =>
            context.effectiveRoles.some(
              (r) => r.scopeType === 'Study' && r.scopeId === studySiteId,
            ),
          );
        if (!accessible) {
          throw new FakeApiError(404, 'PLT_NOT_FOUND');
        }
        if (segments.length === 5 && request.method === 'GET') {
          requireTenantPermission(ctx, tenantId, 'audit.read');
          return queryAudit(request, ctx, url, { kind: 'study', tenantId, studySiteId });
        }
        if (segments[5] === 'export' && request.method === 'GET') {
          requireTenantPermission(ctx, tenantId, 'audit.export');
          return exportAudit(request, ctx, url, { kind: 'study', tenantId, studySiteId });
        }
      }
    }
    return null;
  } catch (error: unknown) {
    if (error instanceof FakeApiError) {
      return problemResponse(error.status, error.errorCode, ctx.correlationId, error.extra);
    }
    throw error;
  }
}
